import React, { useMemo, useState } from "react";
import Icons from "../icons";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* The six selectable report kinds (mirrors backend REPORT_TYPES and the
   CreateReportWizard). A report can include one or many; each adds its own
   sections to the generated PDF. */
const REPORT_TYPES = [
  { id: "test_summary", label: "Test Summary", hint: "Scope, metrics, environment, exit criteria & sign-off" },
  { id: "detailed", label: "Detailed Test", hint: "Every step with before/after evidence screenshots" },
  { id: "defect", label: "Defect", hint: "Failed cases as defects — severity, module, status" },
  { id: "coverage", label: "Test Coverage", hint: "Requirement / ticket ↔ test-case mapping & gaps" },
  { id: "automation", label: "Automation", hint: "Automated run results: pass/fail, steps, timestamps" },
  { id: "progress", label: "Progress / Status", hint: "Execution status, counts and what's left" },
];

/**
 * Modal for picking report types for an execution.
 *
 * Two modes:
 *   mode="view"  (default) — first-time picker: user picks types, clicks
 *                "Create & view" which generates the report PDF + saves types.
 *   mode="edit"  — edit picker: pre-fills the saved types, user can add/remove,
 *                clicks "Save" which only persists the types (no report generated).
 *
 * Props:
 *   execution    : { executionkey, name, status, report_types? }
 *   userkey      : creator's user key (optional)
 *   mode         : "view" | "edit"  (default "view")
 *   onClose      : () => void
 *   onCreated    : (report, chosenTypes) => void  (view mode — after report created)
 *   onTypesSaved : (chosenTypes) => void           (edit mode — after types saved)
 */
export default function ExecutionReportModal({
  execution,
  userkey,
  mode = "view",
  onClose,
  onCreated,
  onTypesSaved,
}) {
  const isEdit = mode === "edit";
  const [types, setTypes] = useState(
    isEdit && Array.isArray(execution?.report_types) ? [...execution.report_types] : []
  );
  const [name, setName] = useState(
    execution?.name ? `${execution.name} — report` : "Execution report"
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = useMemo(
    () => types.length > 0 && (isEdit || name.trim().length > 0) && !submitting,
    [types, name, submitting, isEdit]
  );

  const toggleType = (id) =>
    setTypes((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // View mode: generate the report, open the PDF, then notify parent.
  const createAndView = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          executionkey: execution.executionkey,
          execution_name: execution.name || null,
          report_types: types,
          userkey: userkey || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.data?.reportkey) {
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      const report = body.data;
      window.open(
        `${API_BASE_URL}reports/${encodeURIComponent(report.reportkey)}/pdf`,
        "_blank",
        "noopener,noreferrer"
      );
      onCreated?.(report, types);
      onClose?.();
    } catch (e) {
      setError(e.message || "Could not create the report.");
    } finally {
      setSubmitting(false);
    }
  };

  // Edit mode: just save the chosen types (no report generation).
  const saveTypes = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}executions/${encodeURIComponent(execution.executionkey)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ report_types: types }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      onTypesSaved?.(types);
      onClose?.();
    } catch (e) {
      setError(e.message || "Could not save report types.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = isEdit ? saveTypes : createAndView;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className={`flex ${DIALOG_HEIGHT} w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
        <div className="flex flex-none items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
              <Icons.Report className="h-4 w-4 text-brand-600" />
              {isEdit ? "Edit report types" : "View report"}
            </h2>
            <p className="mt-0.5 truncate text-sm text-slate-500">
              For execution <span className="font-medium text-slate-700">{execution?.name}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Report name — only shown in view mode */}
          {!isEdit && (
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Report name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
              />
            </label>
          )}

          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">
              Report types <span className="font-normal text-slate-400">(pick one or more)</span>
            </p>
            <div className="grid grid-cols-1 gap-2">
              {REPORT_TYPES.map((t) => {
                const active = types.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleType(t.id)}
                    className={[
                      "flex items-start gap-3 rounded-xl border p-3 text-left transition",
                      active
                        ? "border-brand-500 bg-brand-50/60 ring-1 ring-brand-500/30"
                        : "border-slate-200 hover:border-brand-300",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded border",
                        active ? "border-brand-600 bg-brand-600 text-white" : "border-slate-300 bg-white",
                      ].join(" ")}
                    >
                      {active && <Icons.Check className="h-3.5 w-3.5" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900">{t.label}</span>
                      <span className="block text-xs text-slate-500">{t.hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex flex-none items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Icons.Spinner className="h-4 w-4 animate-spin" />
                {isEdit ? "Saving…" : "Creating…"}
              </>
            ) : isEdit ? (
              <>
                <Icons.Check className="h-4 w-4" />
                Save
              </>
            ) : (
              <>
                <Icons.Eye className="h-4 w-4" />
                Create &amp; view
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
